import {spotheroPost} from 'api';
import {type ApiResponse} from 'api/api.types';
import {type UpcomingEvent} from 'api/events/events.types';
import {type Prettify} from 'types/helpers.types';
import {parsePaginationMeta} from 'utils/api';
import dayjs from 'utils/dayjs';
import {
    apiDateTimeFormat,
    displayDateFormat,
    displayTimeMeridiem,
} from 'utils/date-time';
import {sendSentryException} from 'utils/error-handling';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';

import {
    type BatchCompPass,
    type CompPassRecipient,
    type CreateCompPassResponse,
    DISTRIBUTION_MODE,
    type RawCreateCompPassResponse,
} from 'api/comp-passes/comp-pass.types';

interface TransientTimeFrameInput {
    label: string;
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
}

interface CreateCompPassRequestParamsBase {
    events: UpcomingEvent[];
    transientTimeFrames: TransientTimeFrameInput[];
    /** Custom "before" event offset (`"HH:MM"`). Omitted when offsets are off. */
    eventStartsOffset?: string;
    /** Custom "after" event offset (`"HH:MM"`). Omitted when offsets are off. */
    eventEndsOffset?: string;
}

type CreateAssignedCompPassRequestParams = CreateCompPassRequestParamsBase & {
    recipients: CompPassRecipient[];
    distributionMode?: typeof DISTRIBUTION_MODE.ASSIGNED;
    maxInventoryPerPool?: never;
};

type CreateSharedCompPassRequestParams = CreateCompPassRequestParamsBase & {
    distributionMode: typeof DISTRIBUTION_MODE.SHARED;
    maxInventoryPerPool: number;
    recipients?: never;
};

type CreateCompPassRequestParams =
    | CreateAssignedCompPassRequestParams
    | CreateSharedCompPassRequestParams;

const isSharedCompPassRequest = (
    requestParams: CreateCompPassRequestParams
): requestParams is CreateSharedCompPassRequestParams =>
    requestParams.distributionMode === DISTRIBUTION_MODE.SHARED;

const toApiDateTime = (date: string, time: string): string =>
    dayjs(
        `${date} ${time}`,
        `${displayDateFormat} ${displayTimeMeridiem}`
    ).format(apiDateTimeFormat);

/**
 * Formats the request data for creating comp passes.
 */
const formatCreateCompPassRequest = (
    requestParams: CreateCompPassRequestParams
) => {
    const {
        events,
        transientTimeFrames,
        distributionMode,
        eventStartsOffset,
        eventEndsOffset,
    } = requestParams;
    const isSharedDistribution = isSharedCompPassRequest(requestParams);
    const formattedEvents = events.map((event) => event.eventId);
    const formattedTransientTimeframes = transientTimeFrames.map((row) => ({
        startTime: toApiDateTime(row.startDate, row.startTime),
        endTime: toApiDateTime(row.endDate, row.endTime),
        label: row.label,
    }));
    const formattedRecipients = isSharedDistribution
        ? []
        : requestParams.recipients.map((recipient) => {
              return {
                  email: recipient.email,
                  ccEmails: recipient.ccEmail ? [recipient.ccEmail] : [],
                  phoneNumber: recipient.phoneNumber
                      ? recipient.phoneNumber
                      : null,
                  licensePlate: recipient.licensePlate
                      ? recipient.licensePlate
                      : null,
                  quantity: recipient.quantity,
              };
          });
    const formattedData = convertKeysToSnakeCase({
        eventIds: formattedEvents,
        transientTimeframes: formattedTransientTimeframes,
        ...(distributionMode && {distributionMode}),
        ...(!isSharedDistribution && {recipients: formattedRecipients}),
        ...(isSharedDistribution && {
            maxInventoryPerPool: requestParams.maxInventoryPerPool,
        }),
        // Only sent when custom offsets are enabled; absence tells the backend
        // to fall back to the pricing-rule offsets.
        ...(eventStartsOffset && {eventStartsOffset}),
        ...(eventEndsOffset && {eventEndsOffset}),
    });

    return formattedData;
};

/**
 * Formats the response from creating comp passes.
 */
const formatCreateCompPassesResponse = (
    response: ApiResponse<RawCreateCompPassResponse[]>
) => {
    const formattedData = camelizeKeys<
        RawCreateCompPassResponse[],
        BatchCompPass[]
    >(response.data.data.results || []);
    const parsedMeta = parsePaginationMeta(response.data.meta);

    const formattedResponse: Prettify<CreateCompPassResponse> = {
        data: formattedData,
        meta: parsedMeta,
    };

    return formattedResponse;
};

/**
 * Creates a new batch of comp passes for a given facility.
 *
 * @see `/api/v1/facilities/{facilityId}/comp-pass/batches/`
 */
export const createCompPasses = async ({
    facilityId,
    requestParams,
}: {
    facilityId: number;
    requestParams: CreateCompPassRequestParams;
}) => {
    try {
        const formattedData = formatCreateCompPassRequest(requestParams);

        const response = await spotheroPost<RawCreateCompPassResponse[]>(
            `facilities/${facilityId}/comp-pass/batches/`,
            formattedData,
            {},
            false
        );

        const formattedResponse = formatCreateCompPassesResponse(response);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};
